import { Popover, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useParticipant, VideoPlayer, useMeeting } from "@videosdk.live/react-sdk";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import useIsMobile from "../hooks/useIsMobile";
import useIsTab from "../hooks/useIsTab";
import useWindowSize from "../hooks/useWindowSize";
import MicOffSmallIcon from "../icons/MicOffSmallIcon";
import SpeakerIcon from "../icons/SpeakerIcon";
import { getQualityScore, nameTructed } from "../utils/common";
import * as ReactDOM from "react-dom";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import { useSiteContext } from "../context/SiteContext";
import { useBreakoutRooms } from "../context/BreakoutRoomsContext";
import logger from '../utils/logger';

export const CornerDisplayName = ({
  participantId,
  isPresenting,
  displayName,
  isLocal,
  micOn,
  mouseOver,
  isActiveSpeaker,
}) => {
  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 });
  const isXLDesktop = useMediaQuery({ minWidth: 1440 });

  const { height: windowHeight } = useWindowSize();

  const [statsBoxHeightRef, setStatsBoxHeightRef] = useState(null);
  const [statsBoxWidthRef, setStatsBoxWidthRef] = useState(null);

  const [coords, setCoords] = useState({}); // takes current button coordinates

  const statsBoxHeight = useMemo(
    () => statsBoxHeightRef?.offsetHeight,
    [statsBoxHeightRef]
  );

  const statsBoxWidth = useMemo(
    () => statsBoxWidthRef?.offsetWidth,
    [statsBoxWidthRef]
  );

  const analyzerSize = isXLDesktop
    ? 32
    : isLGDesktop
      ? 28
      : isTab
        ? 24
        : isMobile
          ? 20
          : 18;

  const show = useMemo(() => {
    return true;
  }, []);

  const {
    webcamStream,
    micStream,
    screenShareStream,
    getVideoStats,
    getAudioStats,
    getShareStats,
    getShareAudioStats,
    participant,
  } = useParticipant(participantId);

  const statsIntervalIdRef = useRef();
  const [score, setScore] = useState({});
  const [audioStats, setAudioStats] = useState({});
  const [videoStats, setVideoStats] = useState({});

  const updateStats = async () => {
    let stats = [];
    let audioStats = [];
    let videoStats = [];
    if (isPresenting) {
      stats = await getShareStats();
    } else if (webcamStream) {
      stats = await getVideoStats();
    } else if (micStream) {
      stats = await getAudioStats();
    }

    if (webcamStream || micStream || isPresenting) {
      videoStats = isPresenting ? await getShareStats() : await getVideoStats();
      audioStats = isPresenting
        ? await getShareAudioStats()
        : await getAudioStats();
    }

    let score = stats
      ? stats.length > 0
        ? getQualityScore(stats[0])
        : 100
      : 100;

    setScore(score);
    setAudioStats(audioStats);
    setVideoStats(videoStats);
  };

  const qualityStateArray = [
    { label: "", audio: "Audio", video: "Video" },
    {
      label: "Latency",
      audio:
        audioStats && audioStats[0]?.rtt ? `${audioStats[0]?.rtt} ms` : "-",
      video:
        videoStats && videoStats[0]?.rtt ? `${videoStats[0]?.rtt} ms` : "-",
    },
    {
      label: "Jitter",
      audio:
        audioStats && audioStats[0]?.jitter
          ? `${parseFloat(audioStats[0]?.jitter).toFixed(2)} ms`
          : "-",
      video:
        videoStats && videoStats[0]?.jitter
          ? `${parseFloat(videoStats[0]?.jitter).toFixed(2)} ms`
          : "-",
    },
    {
      label: "Packet Loss",
      audio: audioStats
        ? audioStats[0]?.packetsLost
          ? `${parseFloat(
            (audioStats[0]?.packetsLost * 100) / audioStats[0]?.totalPackets
          ).toFixed(2)}%`
          : "-"
        : "-",
      video: videoStats
        ? videoStats[0]?.packetsLost
          ? `${parseFloat(
            (videoStats[0]?.packetsLost * 100) / videoStats[0]?.totalPackets
          ).toFixed(2)}%`
          : "-"
        : "-",
    },
    {
      label: "Bitrate",
      audio:
        audioStats && audioStats[0]?.bitrate
          ? `${parseFloat(audioStats[0]?.bitrate).toFixed(2)} kb/s`
          : "-",
      video:
        videoStats && videoStats[0]?.bitrate
          ? `${parseFloat(videoStats[0]?.bitrate).toFixed(2)} kb/s`
          : "-",
    },
    {
      label: "Frame rate",
      audio: "-",
      video:
        videoStats &&
          (videoStats[0]?.size?.framerate === null ||
            videoStats[0]?.size?.framerate === undefined)
          ? "-"
          : `${videoStats ? videoStats[0]?.size?.framerate : "-"}`,
    },
    {
      label: "Resolution",
      audio: "-",
      video: videoStats
        ? videoStats && videoStats[0]?.size?.width === null
          ? "-"
          : `${videoStats[0]?.size?.width}x${videoStats[0]?.size?.height}`
        : "-",
    },
    {
      label: "Codec",
      audio: audioStats && audioStats[0]?.codec ? audioStats[0]?.codec : "-",
      video: videoStats && videoStats[0]?.codec ? videoStats[0]?.codec : "-",
    },
    {
      label: "Cur. Layers",
      audio: "-",
      video:
        videoStats && !isLocal
          ? videoStats && videoStats[0]?.currentSpatialLayer === null
            ? "-"
            : `S:${videoStats[0]?.currentSpatialLayer || 0} T:${videoStats[0]?.currentTemporalLayer || 0
            }`
          : "-",
    },
    {
      label: "Pref. Layers",
      audio: "-",
      video:
        videoStats && !isLocal
          ? videoStats && videoStats[0]?.preferredSpatialLayer === null
            ? "-"
            : `S:${videoStats[0]?.preferredSpatialLayer || 0} T:${videoStats[0]?.preferredTemporalLayer || 0
            }`
          : "-",
    },
  ];

  useEffect(() => {
    if (webcamStream || micStream || screenShareStream) {
      updateStats();

      if (statsIntervalIdRef.current) {
        clearInterval(statsIntervalIdRef.current);
      }

      statsIntervalIdRef.current = setInterval(updateStats, 500);
    } else {
      if (statsIntervalIdRef.current) {
        clearInterval(statsIntervalIdRef.current);
        statsIntervalIdRef.current = null;
      }
    }

    return () => {
      if (statsIntervalIdRef.current) clearInterval(statsIntervalIdRef.current);
    };
  }, [webcamStream, micStream, screenShareStream]);

  return (
    <>
      <div
        className="absolute bottom-2 left-2 rounded-md flex items-center justify-center p-2 pointer-events-none"
        style={{
          backgroundColor: "#00000066",
          transition: "all 200ms",
          transitionTimingFunction: "linear",
          transform: `scale(${show ? 1 : 0})`,
        }}
      >
        {!micOn && !isPresenting ? (
          <MicOffSmallIcon fillcolor="#FF4B4B" />
        ) : micOn && isActiveSpeaker ? (
          <SpeakerIcon />
        ) : null}
        <p className="text-sm text-white ml-0.5">
          {isPresenting
            ? isLocal
              ? `You are presenting`
              : `${nameTructed(displayName, 15)} is presenting`
            : nameTructed(displayName, 26)}
        </p>
      </div>

      {(webcamStream || micStream || screenShareStream) && (
        <div>
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="absolute bottom-2 right-2 rounded-md p-2 cursor-pointer pointer-events-auto"
          >
            <Popover className="relative">
              {({ close }) => (
                <>
                  <Popover.Button
                    className="rounded-full flex items-center justify-center cursor-pointer"
                    style={{
                      backgroundColor:
                        score > 7
                          ? "#3BA55D"
                          : score > 4
                            ? "#faa713"
                            : "#FF5D5D",
                      width: "12px",
                      height: "12px",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <div></div>
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel
                      style={{ zIndex: 999 }}
                      className="absolute bottom-0 right-0 mb-2 mr-2"
                    >
                      <div
                        ref={setStatsBoxWidthRef}
                        className="absolute bottom-0 right-0"
                      >
                        <div
                          ref={setStatsBoxHeightRef}
                          className="bg-gray-800 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5"
                        >
                          <div
                            className={`p-[9px] flex items-center justify-between rounded-t-lg`}
                            style={{
                              backgroundColor:
                                score > 7
                                  ? "#3BA55D"
                                  : score > 4
                                    ? "#faa713"
                                    : "#FF5D5D",
                            }}
                          >
                            <p className="text-sm text-white font-semibold">{`Quality Score : ${score > 7
                              ? "Good"
                              : score > 4
                                ? "Average"
                                : "Poor"
                              }`}</p>

                            <button
                              className="cursor-pointer text-white hover:bg-[#ffffff33] rounded-full px-1 text-center"
                              onClick={(e) => {
                                e.stopPropagation();
                                close();
                              }}
                            >
                              <XMarkIcon
                                className="text-white"
                                style={{ height: 16, width: 16 }}
                              />
                            </button>
                          </div>
                          <div className="flex">
                            <div className="flex flex-col">
                              {qualityStateArray.map((item, index) => {
                                return (
                                  <div
                                    className="flex"
                                    style={{
                                      borderBottom:
                                        index === qualityStateArray.length - 1
                                          ? ""
                                          : `1px solid #ffffff33`,
                                    }}
                                  >
                                    <div className="flex flex-1 items-center w-[120px]">
                                      {index !== 0 && (
                                        <p className="text-xs text-white my-[6px] ml-2">
                                          {item.label}
                                        </p>
                                      )}
                                    </div>
                                    <div
                                      className="flex flex-1 items-center justify-center"
                                      style={{
                                        borderLeft: `1px solid #ffffff33`,
                                      }}
                                    >
                                      <p className="text-xs text-white my-[6px] w-[80px] text-center">
                                        {item.audio}
                                      </p>
                                    </div>
                                    <div
                                      className="flex flex-1 items-center justify-center"
                                      style={{
                                        borderLeft: `1px solid #ffffff33`,
                                      }}
                                    >
                                      <p className="text-xs text-white my-[6px] w-[80px] text-center">
                                        {item.video}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          </div>
        </div>
      )}
    </>
  );
};

export function ParticipantView({
  participantId,
  isSingleParticipant = false,
  isMobileParticipant = false,
}) {
  const {
    displayName,
    micStream,
    webcamOn,
    micOn,
    isLocal,
    mode,
    isActiveSpeaker,
    participant,
    pinState,
  } = useParticipant(participantId);

  const { localPinnedParticipant } = useSiteContext();
  const isMobile = useIsMobile();
  const { selectedSpeaker, raisedHandsParticipants } = useMeetingAppContext();
  const { isInSameRoom, currentRoom } = useBreakoutRooms();
  const { localParticipant } = useMeeting();
  const micRef = useRef(null);
  const [mouseOver, setMouseOver] = useState(false);
  const [safariSetSinkIdCalled, setSafariSetSinkIdCalled] = useState(false);

  // Check if this participant should have audio/video based on breakout room
  const shouldHaveAudioVideo = useMemo(() => {
    // If not in any breakout room, always allow audio/video
    if (!currentRoom) return true;

    // Always allow self
    if (participantId === localParticipant?.id) return true;

    // Check if participant is in the same room
    return isInSameRoom(participantId);
  }, [currentRoom, participantId, isInSameRoom, localParticipant?.id]);

  // Check if this participant has raised their hand
  const hasRaisedHand = raisedHandsParticipants.some(
    (raisedHand) => raisedHand.participantId === participantId
  );

  // Function to handle Safari setSinkId with user gesture
  const handleSafariSetSinkId = () => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (
      isSafari &&
      micRef.current &&
      selectedSpeaker?.id &&
      !safariSetSinkIdCalled
    ) {
      try {
        micRef.current.setSinkId(selectedSpeaker.id);
        setSafariSetSinkIdCalled(true);
        logger("log", "Safari setSinkId called successfully");
      } catch (err) {
        logger("log", "Safari setSinkId failed:", err);
      }
    }
  };

  useEffect(() => {
    const isFirefox = navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (micRef.current && selectedSpeaker?.id) {
      try {
        if (!isFirefox && !isSafari) {
          // For non-Firefox and non-Safari browsers, setSinkId works normally
          micRef.current.setSinkId(selectedSpeaker.id);
        } else if (isSafari) {
          // For Safari, we need to wait for a user gesture
          // This will be handled when the user interacts with audio controls
          logger("log", 
            "Safari detected: setSinkId will be called on user interaction"
          );
        }
      } catch (err) {
        logger("log", "Setting speaker device failed", err);
      }
    }
  }, [selectedSpeaker]);

  useEffect(() => {
    if (micRef.current) {
      // Only play audio if participant is in the same room or not in any room
      if (micOn && micStream && shouldHaveAudioVideo) {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(micStream.track);
        micRef.current.srcObject = mediaStream;
        micRef.current
          .play()
          .catch((error) =>
            logger("error", "micRef.current.play() failed", error)
          );
      } else {
        // Stop audio if not in same room
        micRef.current.srcObject = null;
        micRef.current.pause();
      }
    }
  }, [micStream, micOn, micRef, shouldHaveAudioVideo]);

  return mode === "SEND_AND_RECV" || mode === "CONFERENCE" ? (
    <div
      onMouseEnter={() => {
        setMouseOver(true);
      }}
      onMouseLeave={() => {
        setMouseOver(false);
      }}
      className={`select-none w-full h-full relative overflow-hidden ${webcamOn ? "bg-black" : "bg-gray-750"
        } ${isSingleParticipant ? "video-cover-single" : "video-cover"} ${isActiveSpeaker ? "border-2 border-[#7700ff]" : ""
        }`}
      style={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <audio
        ref={micRef}
        autoPlay
        muted={isLocal}
        onClick={handleSafariSetSinkId}
        style={{ cursor: "pointer" }}
      />
      {webcamOn ? (
        <div className="absolute inset-0 w-full h-full bg-black flex items-center justify-center">
          {isMobileParticipant ? (
            <div
              className="h-full flex items-center justify-center"
              style={{
                width: "100%",
              }}
            >
              <div
                className="h-full"
                style={{
                  width: "auto",
                  aspectRatio: "9/16",
                }}
              >
                <VideoPlayer
                  participantId={participantId}
                  type="video"
                  containerStyle={{
                    height: "100%",
                    width: "100%",
                  }}
                  className="h-full w-full"
                  classNameVideo="h-full w-full"
                  videoStyle={{
                    objectFit: "contain",
                    width: "100%",
                    height: "100%",
                  }}
                />
              </div>
            </div>
          ) : (
            <VideoPlayer
              participantId={participantId}
              type="video"
              containerStyle={{
                height: "100%",
                width: "100%",
              }}
              className="h-full w-full"
              classNameVideo="h-full w-full"
              videoStyle={{
                objectFit: "contain",
                width: "100%",
                height: "100%",
              }}
            />
          )}
        </div>
      ) : (
        <div
          className="h-full w-full flex items-center justify-center"
          style={{ minWidth: 0, minHeight: 0 }}
        >
          <div
            className={` flex items-center justify-center rounded-full bg-gray-800  h-[52px] w-[52px]`}
          >
            {participant?.metaData?.profileImage ? (
              <img
                alt="Profile3"
                className="w-full h-full rounded-full object-cover"
                src={participant?.metaData?.profileImage}
              />
            ) : (
              <span className="text-2xl text-white">
                {String(displayName).charAt(0).toUpperCase()}
              </span>
            )}

            {/* {isActiveSpeaker && (
              <div className="absolute bottom-2 right-2 rounded-md flex items-center justify-center p-2">
                <p className="text-sm text-white">Active Speaker</p>
                <SpeakerIcon />
              </div>
            )} */}
          </div>
        </div>
      )}

      {/* Raise Hand Overlay */}
      {hasRaisedHand && (
        <div className="absolute top-2 left-2 z-20">
          <div className="rounded-full p-2 shadow-lg">
            <img
              src="/icons/handIcon.png"
              alt="Raised Hand"
              className="w-8 h-8"
            />
          </div>
        </div>
      )}

      {!hasRaisedHand &&
        localPinnedParticipant.includes(participantId) &&
        !pinState.cam && (
          <div className={`absolute left-2 top-2 z-20`}>
            <div className="rounded-full p-2 shadow-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 384 512"
                width="20"
                height="20"
                fill="none"
                stroke="#fff"
                strokeWidth="32"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M368 384c0-70.7-57.3-128-128-128V192h32c8.8 0 16-7.2 16-16V48c0-8.8-7.2-16-16-16H112c-8.8 0-16 7.2-16 16v128c0 8.8 7.2 16 16 16h32v64C73.3 256 16 313.3 16 384c0 8.8 7.2 16 16 16h144v96c0 8.8 7.2 16 16 16s16-7.2 16-16v-96h144c8.8 0 16-7.2 16-16z" />
              </svg>
            </div>
          </div>
        )}

      {!hasRaisedHand && pinState.cam && (
        <div className={`absolute left-2 top-2 z-20`}>
          <div className="rounded-full p-2 shadow-lg">
            <svg
              width="20"
              height="20"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
            >
              <path
                fill="none"
                stroke="#fff"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15.295 19.562L16 22m1-6l3.758 2.098M19 12.5l3.026-.598M7.61 6.3a3 3 0 0 0-3.92 1.3l-1.38 2.79a3 3 0 0 0 1.3 3.91l6.89 3.597a1 1 0 0 0 1.342-.447l3.106-6.211a1 1 0 0 0-.447-1.341zM8 9V2"
              />
            </svg>
          </div>
        </div>
      )}

      {webcamOn && (
        <div className="absolute inset-0">
          <CornerDisplayName
            {...{
              isLocal,
              displayName,
              micOn,
              webcamOn,
              isPresenting: false,
              participantId,
              mouseOver,
              isActiveSpeaker,
            }}
          />
        </div>
      )}
      {!webcamOn && (
        <CornerDisplayName
          {...{
            isLocal,
            displayName,
            micOn,
            webcamOn,
            isPresenting: false,
            participantId,
            mouseOver,
            isActiveSpeaker,
          }}
        />
      )}
    </div>
  ) : null;
}